import { FC } from "react";
import { TextVariant } from "../components/Display/Text";
import {
  OutreachEmailStatuses,
  ExtendedEmailStatuses,
  EmailStatuses,
} from "../types/organizations";
import {
  DiamondSvg,
  TriangleSvg,
  SquareSvg,
  CircleSvg,
} from "../components/Display/Shapes";

import { ThemeMode } from "./contexts/ThemeContext";

export const SECONDS = 1000;
export const MINUTES = 60 * SECONDS;
export const HOURS = 60 * MINUTES;
export const DAYS = 24 * HOURS;

// Light mode colors (default)
const LIGHT_COLORS = {
  backgroundWhite: "#ffffff",
  backgroundPrimary: "#fefdfc",
  backgroundSecondary: "#f4f4f7",
  backgroundSecondaryCard: "#fafafa",
  backgroundBrand: "#e8eaf1",
  backgroundBrandLight: "#5e5eaf14",
  backgroundBrandLightHover: "#5e5eaf0a",
  backgroundBrandDark: "#4d4b6f",
  backgroundBrandTab: "#f2f2fe",
  backgroundBrandTabHover: "#f1eff0",
  backgroundBrandTable: "#f9f9fb",
  backgroundSwitch: "#565577",
  backgroundSwitchActive: "#7871f6",
  backgroundSwitchHover: "#4f4bac",
  borderBrandLight: "#5951ff",
  borderDark: "#e0e0e0",
  borderPrimary: "#eeeeee",
  borderSecondary: "#a3a2bd",
  borderTertiary: "#b4b4b4",
  foregroundBrand: "#5e5eaf",
  foregroundBrandLight: "#5951ff",
  foregroundBrandTable: "#4f4c92",
  foregroundPrimary: "#454262",
  foregroundSecondary: "#686781",
  foregroundSecondaryLight: "#a3a2bd",
  foregroundSecondaryDark: "#5d5f63",
  foregroundTertiary: "#444260",
  foregroundWhite: "#ffffff",
  chartBouncesPrevented: "#000000",
  chartEmailInvalid: "#b63d4f",
  chartEmailValid: "#4ca18a",
  chartEmailQuestionable: "#e4995f",
  chartEmailNoEmail: "#696ac0",
  chartEmailEnriched: "#649cd2",
  chartEmailProspectsChecked: "#696ac0",
  chartAxisColor: "#686783",
  chartAxisLine: "#686783",
  outlineSwitchHover: "#f3f2fb",
  outlineInputHover: "#5a5aa8",
  outlineInputFocused: "#7971ff",
  inputLabel: "#5853ff",
  integrationLoading: "#e4995f",
  integrationConnected: "#4ca18a",
  integrationDisconnected: "#b63d4f",
  dropdownIconColor: "#686783",
  inputOutline: "rgba(0, 0, 0, 0.23)",
  // Toggle and Save Button colors
  toggleInactive: "#5E5D84",
  toggleInactiveHover: "#4e4d71",
  toggleActive: "#7871F6",
  toggleActiveHover: "#5E5EAA",
  saveButtonPrimary: "#5E5EAA",
  saveButtonHover: "#3E3B93",
  saveButtonDisabledBg: "#F3F4F6",
  saveButtonDisabledText: "#6B7280",
  saveButtonDisabledBorder: "#E6E7EB",
  saveButtonDisabledIcon: "#DBDAE3",
  // Status callout surfaces (sync dialog etc.)
  // Section tabs (Outreach folder-tab pattern)
  tabActiveBg: "#f2f2fe",
  tabActiveText: "#5951ff",
  tabInactiveText: "#5e5eaf",
  tabHoverBg: "#f1eff0",
  chipBorder: "rgba(89, 81, 255, 0.24)",
  statusDangerBg: "#FCEEEF",
  statusDangerText: "#5f1221",
  statusDangerIcon: "rgb(198, 47, 76)",
  statusSuccessBg: "#e7f3f2",
  statusSuccessText: "#02382d",
  statusInfoBg: "#e6efff",
  spinnerColor: "#A3A2B9",
};

// Dark mode colors - matched to Outreach's dark theme palette
const DARK_COLORS = {
  backgroundWhite: "#212934",
  backgroundPrimary: "#151a20",
  backgroundSecondary: "#262f3e",
  backgroundSecondaryCard: "#212934",
  backgroundBrand: "#262f3e",
  backgroundBrandLight: "#7971ff14",
  backgroundBrandLightHover: "#7971ff0a",
  backgroundBrandDark: "#262f3e",
  backgroundBrandTab: "#32375b",
  backgroundBrandTabHover: "#3a3f5e",
  backgroundBrandTable: "#262f3e",
  backgroundSwitch: "#565577",
  backgroundSwitchActive: "#7871f6",
  backgroundSwitchHover: "#4f4bac",
  borderBrandLight: "#adadff",
  borderDark: "#3b4757",
  borderPrimary: "#262f3e",
  borderSecondary: "#3b4757",
  borderTertiary: "#3b4757",
  foregroundBrand: "#adadff",
  foregroundBrandLight: "#adadff",
  foregroundBrandTable: "#f1f1f4",
  foregroundPrimary: "#f1f1f4",
  foregroundSecondary: "#bdc1c6",
  foregroundSecondaryLight: "#bdc1c6",
  foregroundSecondaryDark: "#bdc1c6",
  foregroundTertiary: "#f1f1f4",
  foregroundWhite: "#f1f1f4",
  chartBouncesPrevented: "#000000",
  chartEmailInvalid: "#b63d4f",
  chartEmailValid: "#4ca18a",
  chartEmailQuestionable: "#e4995f",
  chartEmailNoEmail: "#696ac0",
  chartEmailEnriched: "#649cd2",
  chartEmailProspectsChecked: "#696ac0",
  chartAxisColor: "#bdc1c6",
  chartAxisLine: "#3b4757",
  outlineSwitchHover: "#3b4757",
  outlineInputHover: "#adadff",
  outlineInputFocused: "#adadff",
  inputLabel: "#adadff",
  integrationLoading: "#e4995f",
  integrationConnected: "#4ca18a",
  integrationDisconnected: "#b63d4f",
  dropdownIconColor: "#bdc1c6",
  inputOutline: "#3b4757",
  // Toggle and Save Button colors
  toggleInactive: "#5E5D84",
  toggleInactiveHover: "#4e4d71",
  toggleActive: "#7871F6",
  toggleActiveHover: "#5E5EAA",
  saveButtonPrimary: "#5959cf",
  saveButtonHover: "#6b63ee",
  saveButtonDisabledBg: "#1b222b",
  saveButtonDisabledText: "#8a919b",
  saveButtonDisabledBorder: "#3b4757",
  saveButtonDisabledIcon: "#8a919b",
  // Status callout surfaces - tinted dark surfaces, Outreach-style
  // Section tabs - sampled from Outreach's dark theme
  tabActiveBg: "#32375b",
  tabActiveText: "#adadff",
  tabInactiveText: "#f1f1f4",
  tabHoverBg: "#262f3e",
  chipBorder: "rgba(173, 173, 255, 0.32)",
  statusDangerBg: "#3a2226",
  statusDangerText: "#f0b8bf",
  statusDangerIcon: "#cf625b",
  statusSuccessBg: "#1c332f",
  statusSuccessText: "#a9e5d6",
  statusInfoBg: "#1e2a3d",
  spinnerColor: "#A3A2B9",
};

// Default export for backward compatibility (light mode)
export const COLORS = LIGHT_COLORS;

// Function to get theme-aware colors
export const getColors = (mode: ThemeMode = "light") => {
  return mode === "dark" ? DARK_COLORS : LIGHT_COLORS;
};

export const SHADOWS = {
  small:
    "0rem 0rem 0.0625rem rgba(69, 66, 98, 0.16), 0rem 0.0625rem 0.125rem rgba(69, 66, 98, 0.16)",
  medium: `
  rgba(55, 50, 73, 0.2) 0rem 0.188rem 0.188rem,
  rgba(55, 50, 73, 0.08) 0rem 0.188rem 0.625rem,
  rgba(55, 50, 73, 0.1) 0rem 0.188rem 0.875rem;
`,
  inset: "inset 2px 0px 0px",
  brand: "#5951ff",
};

export const SPACINGS = {
  none: "0rem",
  xs: "0.25rem",
  sm: "0.5rem",
  md: "0.75rem",
  lg: "1rem",
  xl: "1.5rem",
  xxl: "2rem",
};

export const RADII = {
  none: "0rem",
  xxs: "0.1rem",
  xs: "0.125rem",
  sm: "0.25rem",
  md: "0.5rem",
  lg: "0.75rem",
  xl: "1rem",
  full: "9999px",
};

export const SIZES = {
  switchWidth: "3rem",
  switchHeight: "1.5rem",
  switchThumbWidth: "1rem",
  switchThumbHeight: "1rem",
  progressBarHeight: "1.20rem",
  inviteUserDialogWidth: "30rem",
  // Toggle sizes (24px height, 48px width)
  toggleWidth: "48px",
  toggleHeight: "24px",
  toggleThumbSize: "16px",
  toggleThumbOffset: "4px",
  toggleThumbActiveOffset: "28px",
  // Save button sizes (36px height, 64px width)
  saveButtonBorderRadius: "1.5rem",
  saveButtonWidth: "64px",
  saveButtonHeight: "36px",
  saveButtonPaddingX: "1rem",
  saveButtonPaddingY: "0.5rem",
  spinnerSize: "1.125rem",
  inputLabelFontSize: "1rem",
  inputFieldFontWeight: 400,
  inputFieldFontSize: "0.875rem",
  inputBorderWidth: "1.5px",
  navigationFontWeight: 450,
};

export const GAPS = {
  xs: "0.5rem",
  sm: "1rem",
  md: "2rem",
  lg: "3rem",
  xl: "6rem",
  xxl: "8rem",
};

export const SKELETON_SIZES = {
  xs: "0.5rem",
  sm: "1rem",
  md: "2rem",
  lg: "4rem",
  xl: "8rem",
};

export const ICON_SIZES = {
  md: "1.5rem",
};

export const FONT_FAMILIES = {
  titles:
    '"Work Sans", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  subtitles: '"Inter-Bold", "Inter", "Inter Placeholder", sans-serif',
  regular:
    '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

export const FONT_WEIGHTS = {
  extralight: 100,
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const FONTS: Record<TextVariant, string> = {
  h1: `${FONT_WEIGHTS.bold} 3rem ${FONT_FAMILIES.titles}`,
  h2: `${FONT_WEIGHTS.bold} 2.5rem ${FONT_FAMILIES.titles}`,
  h3: `${FONT_WEIGHTS.semibold} 2rem ${FONT_FAMILIES.titles}`,
  h4: `${FONT_WEIGHTS.semibold} 1.5rem ${FONT_FAMILIES.subtitles}`,
  h5: `${FONT_WEIGHTS.semibold} 1.25rem ${FONT_FAMILIES.titles}`,
  h6: `${FONT_WEIGHTS.semibold} 1rem ${FONT_FAMILIES.subtitles}`,
  sm: `${FONT_WEIGHTS.light} 0.875rem ${FONT_FAMILIES.regular}`,
  md: `${FONT_WEIGHTS.medium} 1rem ${FONT_FAMILIES.regular}`,
  lg: `${FONT_WEIGHTS.semibold} 1.125rem ${FONT_FAMILIES.regular}`,
  xl: `${FONT_WEIGHTS.medium} 1.5rem ${FONT_FAMILIES.titles}`,
};

export const StatusColors: Record<EmailStatuses, string> = {
  prospectsChecked: COLORS.chartEmailProspectsChecked,
  [OutreachEmailStatuses.Valid]: COLORS.chartEmailValid,
  [OutreachEmailStatuses.Invalid]: COLORS.chartEmailInvalid,
  [OutreachEmailStatuses.Questionable]: COLORS.chartEmailQuestionable,
  [OutreachEmailStatuses.NoEmail]: COLORS.chartEmailNoEmail,
  [ExtendedEmailStatuses.EnrichedByBoring]: COLORS.chartEmailEnriched,
  [ExtendedEmailStatuses.BouncesPrevented]: COLORS.chartBouncesPrevented,
};

export const ChartLegendLabels: Record<EmailStatuses, string> = {
  prospectsChecked: "Prospects verified",
  [OutreachEmailStatuses.Valid]: "Valid",
  [OutreachEmailStatuses.Invalid]: "Invalid",
  [OutreachEmailStatuses.Questionable]: "Questionable",
  [OutreachEmailStatuses.NoEmail]: "Missing",
  [ExtendedEmailStatuses.EnrichedByBoring]: "Enriched by boring",
  [ExtendedEmailStatuses.BouncesPrevented]: "Bounces prevented",
};

export const ChartValuesShapes: Record<EmailStatuses, FC> = {
  prospectsChecked: CircleSvg,
  [OutreachEmailStatuses.Valid]: DiamondSvg,
  [OutreachEmailStatuses.Invalid]: TriangleSvg,
  [OutreachEmailStatuses.Questionable]: SquareSvg,
  [OutreachEmailStatuses.NoEmail]: CircleSvg,
  [ExtendedEmailStatuses.EnrichedByBoring]: SquareSvg,
  [ExtendedEmailStatuses.BouncesPrevented]: DiamondSvg,
};

export const DEFAULT_CUSTOM_FIELD_IDS = {
  emailStatusCustomFieldId: 150,
  timestampCustomFieldId: 149,
  enrichedCustomFieldId: 148,
};

export const LOCAL_STORAGE_KEYS = {
  outreachAuthCode: "outreachAuthCode",
  hubspotAuthCode: "hubspotAuthCode",
  hubspotRedirectUri: "hubspotRedirectUri",
  pendingOrganization: "pendingOrganization", // Temporary storage for org returned from OAuth
};
